import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useReportAssistantAnswerMutation } from "ai-assistant/_api/ai-assistant-api-service";
import AnswerFeedback, { MaximumAnswerFeedbackCommentLength } from "ai-assistant/feedback/answer-feedback";
import {
  AnswerFeedbackCategory,
  AnswerFeedbackReason,
  answerFeedbackReasons,
  categoryOfAnswerFeedbackReason,
  describeAnswerFeedbackReason,
} from "ai-assistant/feedback/answer-feedback-reason";
import React from "react";

const FeedbackForm = styled("form")(({ theme }) => ({
  marginTop: theme.spacing(1),
  paddingTop: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const QuietButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.caption.fontSize,
  padding: `0 ${theme.spacing(0.5)}`,
  minWidth: 0,
  textTransform: "none",
}));

export interface AnswerFeedbackControlProps {
  conversationId: string;
  /** The trace id the answer's reply carried: how the report names the answer. */
  traceId: string;
  /** What the reader already said about this answer, replayed with the conversation. */
  feedback?: AnswerFeedback;
}

/** What the reader is told once a report is in, so they know it went somewhere. */
function acknowledgement(feedback: AnswerFeedback): string {
  const reason = describeAnswerFeedbackReason(feedback.reason);
  return feedback.category === AnswerFeedbackCategory.Access
    ? `Reported: "${reason}". This went to the people who look after access, not only answer quality.`
    : `Reported: "${reason}". Thank you - the team that looks after the assistant reads these.`;
}

/**
 * Lets the reader say an answer was wrong, and why, without leaving the panel (#5204).
 *
 * Inline rather than in a dialog. The panel is itself a dialog below `lg`, and an MUI modal on
 * top of it makes the page behind inert; more to the point, the answer being reported should
 * stay in view while the reader says what is wrong with it.
 *
 * Collapsed to one quiet button until asked for. A control at full size under every answer is
 * noise around the answers that were fine, which are most of them.
 */
export default function AnswerFeedbackControl(props: AnswerFeedbackControlProps): React.JSX.Element {
  const [report, { isLoading }] = useReportAssistantAnswerMutation();
  const [recorded, setRecorded] = React.useState<AnswerFeedback | undefined>(props.feedback);
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState<AnswerFeedbackReason | undefined>(props.feedback?.reason);
  const [comment, setComment] = React.useState(props.feedback?.comment ?? "");
  const [failed, setFailed] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const choicesRef = React.useRef<HTMLDivElement>(null);
  // Set when the form closes, so focus goes back to the button once it is drawn again - and not on
  // first render, where nothing was moved and nothing should be taken.
  const returnFocusRef = React.useRef(false);
  const formId = React.useId();
  const commentId = React.useId();

  // Into the form when it opens, so a keyboard user is at the choice they opened it to make; back
  // to where they were when it closes, rather than to the top of the page the form just left.
  React.useEffect(() => {
    if (open) {
      choicesRef.current?.querySelector<HTMLInputElement>("input")?.focus();
    } else if (returnFocusRef.current) {
      returnFocusRef.current = false;
      triggerRef.current?.focus();
    }
  }, [open]);

  const close = (): void => {
    returnFocusRef.current = true;
    setOpen(false);
    setFailed(false);
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (reason === undefined) return;
    setFailed(false);
    const trimmed = comment.trim();
    const request = {
      conversationId: props.conversationId,
      traceId: props.traceId,
      reason,
      comment: trimmed.length > 0 ? trimmed : undefined,
    };
    try {
      const saved = await report(request).unwrap();
      setRecorded(saved);
      close();
    } catch (error) {
      console.error("Could not report an assistant answer:", error);
      setFailed(true);
    }
  };

  if (!open) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
        {recorded !== undefined && (
          <Typography role="status" variant="caption" color="text.secondary">
            {acknowledgement(recorded)}
          </Typography>
        )}
        <QuietButton
          ref={triggerRef}
          size="small"
          startIcon={recorded === undefined ? <FlagOutlinedIcon fontSize="inherit" /> : undefined}
          aria-expanded={false}
          aria-controls={formId}
          onClick={() => setOpen(true)}
        >
          {recorded === undefined ? "Report a problem with this answer" : "Change report"}
        </QuietButton>
      </Box>
    );
  }

  return (
    <FeedbackForm id={formId} aria-label="Report a problem with this answer" onSubmit={event => { void submit(event); }}>
      <FormControl component="fieldset" required>
        <FormLabel component="legend" sx={{ typography: "body2" }}>
          What was wrong with this answer?
        </FormLabel>
        <RadioGroup
          ref={choicesRef}
          value={reason === undefined ? "" : String(reason)}
          onChange={event => setReason(Number(event.target.value) as AnswerFeedbackReason)}
        >
          {answerFeedbackReasons.map(choice => (
            <FormControlLabel
              key={choice}
              value={String(choice)}
              control={<Radio size="small" />}
              label={<Typography variant="body2">{describeAnswerFeedbackReason(choice)}</Typography>}
            />
          ))}
        </RadioGroup>
      </FormControl>
      {reason !== undefined && categoryOfAnswerFeedbackReason(reason) === AnswerFeedbackCategory.Access && (
        <Typography variant="caption" component="p" color="text.secondary">
          This is treated as a possible security problem and goes straight to the people who look after access.
        </Typography>
      )}
      <TextField
        id={commentId}
        label="Anything to add? (optional)"
        // Clear of the last reason: its label wraps on a narrow panel and the outlined field's
        // floating label otherwise sits right against it.
        sx={{ mt: 2 }}
        multiline
        minRows={2}
        fullWidth
        size="small"
        value={comment}
        onChange={event => setComment(event.target.value)}
        slotProps={{ htmlInput: { maxLength: MaximumAnswerFeedbackCommentLength } }}
        helperText="Read by the Attorney Hub team, with the record of this answer."
      />
      {failed && (
        <Typography role="alert" variant="caption" component="p" color="error" sx={{ mt: 0.5 }}>
          That report did not go through. Please try again.
        </Typography>
      )}
      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        <Button type="submit" size="small" variant="contained" disabled={reason === undefined || isLoading}>
          {isLoading ? "Sending..." : "Send report"}
        </Button>
        <Button type="button" size="small" onClick={close} disabled={isLoading}>
          Cancel
        </Button>
      </Box>
    </FeedbackForm>
  );
}
